import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';

const WebhookList = () => {
    const { token } = useAuth();
    const [webhooks, setWebhooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchWebhooks = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/webhooks`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch webhooks');
            const data = await response.json();
            setWebhooks(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchWebhooks();
    }, [fetchWebhooks]);

    const handleTestWebhook = async (webhookKey) => {
        const confirmTest = window.confirm(`Send a test message to ${webhookKey}? This will post a visible message to the channel.`);
        if (!confirmTest) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/webhooks/test`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ webhook_key: webhookKey })
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Test failed');

            alert(`Success: ${data.message}`);
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading Configuration...</div>;
    if (error) return <div className="p-8 text-center text-red-500">Error: {error}</div>;

    return (
        <div className="space-y-6">
            <header className="flex justify-between items-center border-b border-slate-200 dark:border-slate-700 pb-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">System Webhooks</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Status of configured Discord integrations (Environment Variables).
                    </p>
                </div>
                <div className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    Read Only
                </div>
            </header>

            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                        <tr>
                            <th className="px-6 py-4">Service Name</th>
                            <th className="px-6 py-4">Environment Key</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700 text-slate-700 dark:text-slate-300">
                        {webhooks.map((hook) => (
                            <tr key={hook.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="font-medium text-slate-900 dark:text-white">{hook.name}</div>
                                    <div className="text-xs text-slate-500">{hook.description}</div>
                                </td>
                                <td className="px-6 py-4 font-mono text-xs text-slate-500">
                                    {hook.id}
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                        ${hook.status === 'Active'
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                        }`}>
                                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 
                                            ${hook.status === 'Active' ? 'bg-emerald-500' : 'bg-red-500'}
                                        `}></span>
                                        {hook.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    {hook.status === 'Active' && (
                                        <button
                                            onClick={() => handleTestWebhook(hook.id)}
                                            className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40 dark:text-indigo-400 px-3 py-1.5 rounded-md transition-colors"
                                        >
                                            Test Connection
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
                <p><strong>Note:</strong> These webhooks are configured via server environment variables (.env). To add or change them, please contact the server administrator.</p>
            </div>
        </div>
    );
};

export default WebhookList;
